// src/pages/Dashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API_CONFIG } from '../App';
import { Container, Row, Col, Card, Button, Table, Badge } from 'react-bootstrap';
import './Dashboard.css';

function Dashboard() {
  const { loginUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingBookings: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    balanceRevenue: 0
  });
  const [hotelStats, setHotelStats] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.CLIENTS_URL, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      const data = await response.json();
      
      if (data.status === 'success' || data.success) {
        const bookings = data.data || [];
        calculateStats(bookings);
        calculateHotelStats(bookings);
        setRecentBookings(bookings.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (bookings) => {
    const today = new Date().toISOString().split('T')[0];
    const total = bookings.length;
    const upcoming = bookings.filter(b => b.check_in_date >= today).length;
    const totalRevenue = bookings.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
    const balanceRevenue = bookings.reduce((sum, b) => sum + (parseFloat(b.balance_amount) || 0), 0);
    const pendingPayments = bookings.filter(b => (parseFloat(b.balance_amount) || 0) > 0).length;

    setStats({
      totalBookings: total,
      upcomingBookings: upcoming,
      totalRevenue: totalRevenue,
      balanceRevenue: balanceRevenue,
      pendingPayments: pendingPayments
    });
  };

  const calculateHotelStats = (bookings) => {
    const hotelCounts = {};
    bookings.forEach(booking => {
      const hotelName = booking.hotel || 'Unknown';
      hotelCounts[hotelName] = (hotelCounts[hotelName] || 0) + 1;
    });

    const hotelStatsArray = Object.entries(hotelCounts).map(([name, count]) => ({
      name,
      count,
      percentage: bookings.length > 0 ? Math.round((count / bookings.length) * 100) : 0
    }));

    setHotelStats(hotelStatsArray);
  };

  const statCards = [
    { id: 'total', icon: '📋', label: 'Total Bookings', value: stats.totalBookings, change: '+12%', changeColor: 'success' },
    { id: 'upcoming', icon: '📅', label: 'Upcoming Bookings', value: stats.upcomingBookings, change: '+8%', changeColor: 'success' },
    { id: 'revenue', icon: '💰', label: 'Total Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, change: '+15%', changeColor: 'success' },
    { id: 'pending', icon: '⏳', label: 'Pending Payments', value: `${stats.pendingPayments} (₹${stats.balanceRevenue})`, change: '-3%', changeColor: 'danger' },
  ];

  return (
    <Container fluid className="dashboard-page py-3 fade-in">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold text-primary-dark">📊 Dashboard</h2>
          <p className="text-muted">Welcome back, {loginUser}! Here's your booking overview.</p>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading...</div>
      ) : (
        <>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            {statCards.map(stat => (
              <Col xs={12} sm={6} lg={3} key={stat.id}>
                <Card className="h-100 card-shadow border-0 rounded-4">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="stat-icon-wrapper me-3">
                        <span className="fs-2">{stat.icon}</span>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="text-muted small mb-0">{stat.label}</h5>
                        <h3 className="fw-bold text-primary-dark mb-0">{stat.value}</h3>
                        <Badge bg={stat.changeColor} className="rounded-pill mt-1">
                          {stat.change}
                        </Badge>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Charts Grid */}
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="card-shadow border-0 rounded-4 h-100">
                <Card.Body>
                  <h5 className="fw-bold text-primary-dark mb-3">🏨 Booking by Hotels</h5>
                  {hotelStats.length === 0 ? (
                    <p className="text-muted text-center py-3">No booking data available</p>
                  ) : (
                    <div className="hotel-stats">
                      {hotelStats.map((hotel, index) => (
                        <div key={index} className="mb-2">
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="fw-semibold">{hotel.name}</span>
                            <span className="text-primary-dark fw-bold">{hotel.count}</span>
                          </div>
                          <div className="progress" style={{ height: '8px' }}>
                            <div 
                              className="progress-bar bg-primary-dark" 
                              style={{ width: `${hotel.percentage}%`, borderRadius: '4px' }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="card-shadow border-0 rounded-4 h-100">
                <Card.Body>
                  <h5 className="fw-bold text-primary-dark mb-3">⚡ Quick Actions</h5>
                  <div className="d-flex flex-column gap-2">
                    <Button 
                      variant="primary" 
                      className="rounded-3 py-2 text-start d-flex align-items-center"
                      onClick={() => navigate('/new-booking')}
                    >
                      <span className="me-2">📝</span> New Booking
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      className="rounded-3 py-2 text-start d-flex align-items-center"
                      onClick={() => navigate('/all-bookings')}
                    >
                      <span className="me-2">📋</span> View All Bookings
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      className="rounded-3 py-2 text-start d-flex align-items-center"
                      onClick={() => navigate('/upcoming')}
                    >
                      <span className="me-2">📅</span> Upcoming Bookings
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Recent Bookings */}
          <Row>
            <Col>
              <Card className="card-shadow border-0 rounded-4">
                <Card.Body>
                  <h5 className="fw-bold text-primary-dark mb-3">📋 Recent Bookings</h5>
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-primary-dark text-white">
                        <tr>
                          <th>Guest</th>
                          <th>Hotel</th>
                          <th>Check-in</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.map((booking) => (
                          <tr key={booking.id}>
                            <td>
                              <strong>{booking.username}</strong>
                              <br />
                              <small className="text-muted">{booking.phone}</small>
                            </td>
                            <td>{booking.hotel}</td>
                            <td>{booking.check_in_date}</td>
                            <td className="fw-bold text-success">₹{booking.total_amount || 0}</td>
                            <td>
                              <Badge bg={booking.status === 'confirmed' ? 'success' : 'warning'} className="rounded-pill">
                                {booking.status || 'Confirmed'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                        {recentBookings.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">No bookings yet</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default Dashboard;